/**
 * Settings → Team & roles route.
 *
 * Composes members + invites and active sessions with their dialogs.
 * Per ADR-0011 + ADR-0030:
 *
 *   - Members table is one combined list (active members + pending
 *     invites). Founder/Operator can invite; only Founders see the
 *     destructive bulk-revoke action on Active Sessions.
 *   - Role assignment uses the granular roles defined in Settings ▸
 *     Roles & org (ADR-0030); the ChangeRoleDialog reads `roles.list`
 *     and saves via `roles.assign`. The legacy read-only 3-role
 *     capability matrix was removed — it contradicted dynamic roles.
 *   - Active Sessions is Founder-only for revoke; everyone sees the list.
 *
 * Step-up middleware is currently a passthrough (`stepUpProcedure` in
 * `apps/api/src/trpc.ts`). When the OTP step-up wires in, the
 * mutations on this page will start prompting for re-OTP — no UI
 * change here.
 */
import { useState } from "react";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Alert, ConfirmDialog } from "@flint/ui";
import { hasPermission } from "@flint/api-contract";
import { trpc } from "../../../../lib/trpc.ts";
import { meQueryOptions } from "../../../../lib/session.ts";
import { WorkspaceShell } from "../../../../features/workspace/index.ts";
import { SettingsShell } from "../../../../features/settings/index.ts";
import {
  TeamMembersTable,
  InviteDialog,
  ChangeRoleDialog,
  ActiveSessionsTable,
  type MemberRow,
  type PendingInviteRow,
  type SessionRow,
} from "../../../../features/settings/team/index.ts";

export const Route = createFileRoute("/(workspace)/settings/team/")({
  // URL-level `team.manage` gate — see /settings/company/index.tsx for rationale.
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQueryOptions).catch(() => null);
    const perms = me?.company?.permissions ?? [];
    if (!hasPermission(perms, "team.manage")) {
      throw notFound();
    }
  },
  component: TeamPage,
});

function TeamPage() {
  const me = useQuery(meQueryOptions);
  const utils = trpc.useUtils();
  const team = trpc.team.list.useQuery({});
  const sessions = trpc.team.listSessions.useQuery(
    {},
    {
      // Sessions are Founder-only; the procedure throws FORBIDDEN otherwise.
      // Disable the query when we know the user isn't a Founder so we don't
      // spam the API with 403s on every render.
      enabled: me.data?.company?.role === "Founder",
    },
  );

  const [inviteOpen, setInviteOpen] = useState(false);
  const [changeRoleFor, setChangeRoleFor] = useState<MemberRow | null>(null);
  const [confirmRevoke, setConfirmRevoke] = useState<MemberRow | null>(null);
  const [confirmCancelInvite, setConfirmCancelInvite] = useState<PendingInviteRow | null>(null);
  const [confirmRevokeSession, setConfirmRevokeSession] = useState<SessionRow | null>(null);
  const [confirmRevokeAll, setConfirmRevokeAll] = useState(false);

  const cancelInvite = trpc.team.cancelInvite.useMutation({
    onSuccess: async () => {
      await utils.team.list.invalidate();
      setConfirmCancelInvite(null);
    },
  });
  const resendInvite = trpc.team.resendInvite.useMutation({
    onSuccess: async () => {
      await utils.team.list.invalidate();
    },
  });
  const revoke = trpc.team.revokeMembership.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.team.list.invalidate(),
        utils.billing.overview.invalidate(),
      ]);
      setConfirmRevoke(null);
    },
  });
  const revokeSession = trpc.team.revokeSession.useMutation({
    onSuccess: async () => {
      await utils.team.listSessions.invalidate();
      setConfirmRevokeSession(null);
    },
  });
  const revokeAllSessions = trpc.team.revokeAllSessions.useMutation({
    onSuccess: async () => {
      await utils.team.listSessions.invalidate();
      setConfirmRevokeAll(false);
    },
  });

  // Seat-limit data — used to disable the invite button when the plan
  // ceiling is reached (UI guard mirrors the server-side SeatLimitExceededError).
  const billing = trpc.billing.overview.useQuery({});

  const isFounder = me.data?.company?.role === "Founder";
  const busy =
    cancelInvite.isPending ||
    resendInvite.isPending ||
    revoke.isPending ||
    revokeSession.isPending ||
    revokeAllSessions.isPending;

  return (
    <WorkspaceShell breadcrumbs={[{ label: "Settings" }, { label: "Team & roles" }]}>
      <SettingsShell
        activeSection="team"
        pageTitle="Team & roles"
        pageDescription="Invite teammates, manage their roles, and review active sessions. Phone-based invitations land via WhatsApp."
      >
        {team.isLoading ? (
          <p className="text-sm text-muted">Loading members…</p>
        ) : team.error ? (
          <Alert intent="bad" title="Couldn't load team" description={team.error.message} />
        ) : team.data ? (
          <TeamMembersTable
            members={team.data.members}
            pendingInvites={team.data.pendingInvites}
            busy={busy}
            seatsUsed={billing.data?.usage.seats.used}
            seatLimit={billing.data?.usage.seats.limit}
            onInvite={() => setInviteOpen(true)}
            onChangeRole={(m) => setChangeRoleFor(m)}
            onRevokeMembership={(m) => setConfirmRevoke(m)}
            onResendInvite={(i) => resendInvite.mutate({ inviteId: i.inviteId })}
            onCancelInvite={(i) => setConfirmCancelInvite(i)}
          />
        ) : null}

        {sessions.isLoading ? (
          <p className="text-sm text-muted">Loading sessions…</p>
        ) : sessions.error ? (
          isFounder ? (
            <Alert
              intent="bad"
              title="Couldn't load sessions"
              description={sessions.error.message}
            />
          ) : null
        ) : sessions.data ? (
          <ActiveSessionsTable
            sessions={sessions.data.sessions}
            isFounder={isFounder}
            busy={busy}
            onRevoke={(sessionId) => {
              const target = sessions.data.sessions.find((s) => s.sessionId === sessionId);
              if (target) setConfirmRevokeSession(target);
            }}
            onRevokeAll={() => setConfirmRevokeAll(true)}
          />
        ) : null}

        {/* ── Dialogs ─────────────────────────────────────────────── */}
        <InviteDialog
          open={inviteOpen}
          onOpenChange={setInviteOpen}
          onInvited={async () => {
            await Promise.all([
              utils.team.list.invalidate(),
              utils.billing.overview.invalidate(),
            ]);
          }}
        />

        <ChangeRoleDialog
          open={!!changeRoleFor}
          onOpenChange={(open) => {
            if (!open) setChangeRoleFor(null);
          }}
          member={changeRoleFor}
          onChanged={async () => {
            await utils.team.list.invalidate();
          }}
        />

        <ConfirmDialog
          open={!!confirmRevoke}
          onOpenChange={(open) => {
            if (!open) setConfirmRevoke(null);
          }}
          title="Revoke membership"
          description={
            confirmRevoke
              ? `Remove ${confirmRevoke.user.name} from this workspace? They lose all access immediately.`
              : ""
          }
          intent="danger"
          confirmLabel="Revoke"
          onConfirm={() => {
            if (confirmRevoke) revoke.mutate({ membershipId: confirmRevoke.membershipId });
          }}
        />

        <ConfirmDialog
          open={!!confirmCancelInvite}
          onOpenChange={(open) => {
            if (!open) setConfirmCancelInvite(null);
          }}
          title="Cancel invite"
          description={
            confirmCancelInvite
              ? `Cancel the invitation to ${confirmCancelInvite.phone}? The link they received stops working.`
              : ""
          }
          intent="danger"
          confirmLabel="Cancel invite"
          onConfirm={() => {
            if (confirmCancelInvite)
              cancelInvite.mutate({ inviteId: confirmCancelInvite.inviteId });
          }}
        />

        <ConfirmDialog
          open={!!confirmRevokeSession}
          onOpenChange={(open) => {
            if (!open) setConfirmRevokeSession(null);
          }}
          title="Revoke session"
          description={
            confirmRevokeSession
              ? `Sign out ${confirmRevokeSession.userName}'s session? They'll need to OTP-verify again on next visit.`
              : ""
          }
          intent="danger"
          confirmLabel="Revoke session"
          onConfirm={() => {
            if (confirmRevokeSession)
              revokeSession.mutate({ sessionId: confirmRevokeSession.sessionId });
          }}
        />

        <ConfirmDialog
          open={confirmRevokeAll}
          onOpenChange={setConfirmRevokeAll}
          title="Sign out everywhere"
          description="Revoke every active session across all members? Everyone (including you) will need to OTP-verify again."
          intent="danger"
          confirmLabel="Sign out everywhere"
          onConfirm={() => revokeAllSessions.mutate({})}
        />
      </SettingsShell>
    </WorkspaceShell>
  );
}
